import React from "react";
import * as H from "../../hooks";
import * as C from "../../Common";
import { TB, T } from "../../Constants";
import { Dialog } from "@material-ui/core";
import renderInContainer from "./getContainer";
import Draggable from 'react-draggable';
import Paper from '@material-ui/core/Paper';
import { Resizable, ResizableProps, ResizeCallbackData } from 'react-resizable';
import 'react-resizable/css/styles.css';

//#region Types
export type StyleModalProps = {
    autofocus?: boolean;
    isFullScreen?: boolean;
    /** Title of the modal, shown in the header */
    title?: string;
    /** If true, the focus trap will not prevent focus from leaving the focus trap while open. */
    disableEnforceFocus?: boolean
    /** Fixed size the modal body cannot outgrow */
    maxBodyHeight?: string;
    /** If true, hitting escape will not fire the onClose callback. */
    disableEscapeKeyDown?: boolean;
    /** Fixed height for the body of the modal */
    height?: string;
    /** Width size of the modal */
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    /** Callback for quitting the modal */
    onQuit?: () => void;
    /** Custom class to the root Modal component */
    className?: string;
    /** Should the popup be hidden, but still active */
    hidden?: boolean;
    /** Extra content to display in the header */
    extra_header?: React.ReactNode;
    /** Only show the extra header, and ignore the title ? */
    only_extra_header?: boolean;
}

export type BlankModalFnProps = StyleModalProps & {
    showHeader?: boolean;
    footer?: React.ReactNode;
    children?: React.ReactNode;
    renderFooter?: (resolve: T.PromiseResolve) => React.ReactNode;
    renderContent?: (resolve: T.PromiseResolve) => React.ReactNode | undefined;
}

export interface BlankModalProps extends Omit<BlankModalFnProps, "renderContent"> {
    onQuit?: () => void;
}

const MODAL_SIZES = {
    xs: { maxWidth: '90vw', initialWidth: 600, initialHeight: 200 },
    sm: { maxWidth: '90vw', initialWidth: 600, initialHeight: 650 },
    md: { maxWidth: '90vw', initialWidth: 960, initialHeight: '90vh' },
    lg: { maxWidth: '90vw', initialWidth: 1275, initialHeight: 800 },
    xl: { maxWidth: '90vw', initialWidth: 1275, initialHeight: 800 }
};

interface DraggablePaperProps {
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    style?: React.CSSProperties;
    children?: React.ReactNode;
    maxHeight?: string;
    [key: string]: any;
}

interface CustomPaperProps {
    className?: string;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
}

const DraggablePaper: React.FC<DraggablePaperProps> = ({ size, maxHeight, ...paperProps }) => {
    const modalSize = React.useMemo(() => size || 'sm', [size]);
    const max_height = React.useMemo(() => maxHeight || '90vh', [maxHeight]);
    const [windowSize, setWindowSize] = React.useState({ width: window.innerWidth, height: window.innerHeight });
    const [isAutoHeight, setIsAutoHeight] = React.useState(true);
    const paperRef = React.useRef<HTMLDivElement>(null);

    const [dimensions, setDimensions] = React.useState(() => {
        const initialWidth = Math.min(
            MODAL_SIZES[modalSize].initialWidth,
            window.innerWidth * (parseInt(MODAL_SIZES[modalSize].maxWidth) / 100)
        );

        const initialHeight = modalSize === 'md' ? window.innerHeight * 0.8 : Math.min(
            MODAL_SIZES[modalSize].initialHeight,
            window.innerHeight * (parseInt(max_height) / 100)
        );
        return { width: initialWidth, height: initialHeight };
    });

    React.useEffect(() => {
        const handleResize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });

            const newHeight = window.innerHeight * (parseInt(max_height) / 100);
            const newWidth = Math.min(
                MODAL_SIZES[modalSize].initialWidth,
                window.innerWidth * (parseInt(MODAL_SIZES[modalSize].maxWidth) / 100)
            );

            setDimensions(prev => ({ width: Math.min(prev.width, newWidth), height: Math.min(prev.height, newHeight) }));
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [modalSize, dimensions]);

    const onResize = React.useCallback((e: React.SyntheticEvent, data: ResizeCallbackData) => {
        const { size, handle } = data;
        const maxWidth = windowSize.width - 20;
        const maxHeight = windowSize.height - 20;

        // Apply the speedFactor only for the left and top directions
        const speedFactor = 2;
        let newWidth: number, newHeight: number;

        if (handle.includes('w')) {
            // Left resizing - faster
            const widthDiff = (size.width - dimensions.width) * speedFactor;
            newWidth = Math.max(Math.min(dimensions.width + widthDiff, maxWidth), 200);
        }
        // Right resizing - normal
        else newWidth = Math.max(Math.min(size.width, maxWidth), 200);

        if (handle.includes('n') || handle.includes('s')) {
            if (isAutoHeight) {
                // Utilise la hauteur actuelle du Paper comme point de départ
                const currentHeight = paperRef.current?.clientHeight || dimensions.height;
                setDimensions(prev => ({ ...prev, height: currentHeight }));
                setIsAutoHeight(false);
                return;
            }

            if (handle.includes('n')) {
                // Top resizing - faster
                const heightDiff = (size.height - dimensions.height) * speedFactor;
                newHeight = Math.max(Math.min(dimensions.height + heightDiff, maxHeight), 100);
            }
            // Bottom resizing - normal
            else newHeight = Math.max(Math.min(size.height, maxHeight), 100);
        }

        setDimensions({ width: newWidth, height: newHeight });
    }, [windowSize, dimensions, isAutoHeight]);

    const props = React.useMemo(() => ({
        minConstraints: [200, 100] as ResizableProps['minConstraints'],
        resizeHandles: ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'] as ResizableProps['resizeHandles'],
        maxConstraints: [windowSize.width - 20, windowSize.height - 20] as ResizableProps['maxConstraints'],
    }), [windowSize]);

    return <Draggable
        bounds="body"
        handle=".modal-header"
        cancel='.modal-controls, [class*="MuiDialogContent-root"], .react-resizable-handle, button, input, textarea, select, .modal-body, .modal-footer'
    >
        <Resizable
            onResize={onResize}
            width={dimensions.width}
            height={dimensions.height}
            resizeHandles={props.resizeHandles}
            minConstraints={props.minConstraints}
            maxConstraints={props.maxConstraints}
            handle={(h, ref) => (
                <div
                    ref={ref}
                    className={`custom-handle custom-handle-${h}`}
                    onMouseMove={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        document.body.style.userSelect = 'none';
                    }}
                />
            )}
        >
            <Paper
                {...paperProps}
                ref={paperRef}
                style={{
                    ...paperProps.style,
                    width: `${dimensions.width}px`,
                    height: isAutoHeight && !paperProps.height ? 'auto' : `${dimensions.height}px`,
                    position: 'relative',
                    transform: 'none'
                }}
            />
        </Resizable>
    </Draggable>;
};

const BlankModal: React.FC<BlankModalProps> = ({
    title,
    showHeader,
    children,
    footer,
    isFullScreen = false,
    size = "sm",
    onQuit,
    autofocus = true,
    maxBodyHeight,
    ...props
}) => {
    const lg = H.useLanguage();
    const [isMaximized, setIsMaximized] = React.useState(isFullScreen);

    const bodyHeight = React.useMemo(() => {
        if (isFullScreen) return {};
        if (props.height) return { height: props.height };
        return {}; // Permet au contenu de définir sa propre hauteur
    }, [props.height, isFullScreen]);

    //#region Modal parts
    const isHtml = React.useCallback(val => TB.isDomElement(val) || React.isValidElement(val), []);

    const modalHeader = React.useMemo(() => (title || showHeader) && (
        <div className="modal-header">
            {!props.only_extra_header && <h5 className="modal-title">{lg.getStaticText(title)}</h5>}
            {props.extra_header && <div className="ms-2" children={props.extra_header} />}
            <div className="modal-controls">
                <button
                    className="maximize-button"
                    onClick={() => setIsMaximized(!isMaximized)}
                >
                    <i className={`fa fa-${isMaximized ? 'compress' : 'expand'}`} />
                </button>
                <button className="btn-close" onClick={() => onQuit()}></button>
            </div>
        </div>
    ), [lg, showHeader, title, props.extra_header, props.only_extra_header, onQuit, isMaximized]);

    const hasFooter = React.useMemo(() => isHtml(footer), [isHtml, footer]);

    const modalBody = React.useMemo(() => (
        <div className={`modal-body ${isMaximized ? 'maximized' : ''}`} style={{ ...bodyHeight, overflow: 'auto' }}>
            {children}
        </div>
    ), [children, isMaximized, bodyHeight]);

    const modalFooter = React.useMemo(() => !isHtml(footer) ? undefined : (
        <div className="modal-footer" style={{ flexShrink: 0 }}>
            {footer}
        </div>
    ), [footer, isHtml]);
    //#endregion

    return <Dialog
        open
        fullWidth
        hidden={props.hidden}
        maxWidth={false}
        onClose={() => onQuit()}
        fullScreen={isMaximized}
        className={`${props.className} resizable-dialog ${isMaximized ? 'maximized' : ''}`}
        disableAutoFocus={!autofocus}
        disableEnforceFocus={props.disableEnforceFocus}
        PaperComponent={!isMaximized ? DraggablePaper : undefined}
        PaperProps={{
            maxHeight: maxBodyHeight,
            className: "animated_scale_in draggable-dialog",
            size: size,
            style: { maxWidth: 'none' },
        } as CustomPaperProps}
        disableEscapeKeyDown={props.disableEscapeKeyDown}
    >
        <div className={`modal-content ${hasFooter ? "" : "border-bottom-0"} ${isFullScreen ? "h-100" : ""}`}>
            {modalHeader}
            {modalBody}
            {modalFooter}
        </div>
    </Dialog>
};

const BlankModalReduxWrapper: React.FC<BlankModalProps> = ({ ...props }) => <C.ReduxWrapper children={<BlankModal {...props} />} />;

export default BlankModalReduxWrapper;

export const renderBlankModal = <A,>(params?: BlankModalFnProps) => new Promise<A | null>(resolve => {
    const [render, dismount] = renderInContainer();

    if (!TB.validObject(params)) params = {};

    if (render && dismount) {
        let onQuit = () => dismount(() => resolve(null));
        let onResolve = value => dismount(() => resolve(value));

        let footer = params.footer;
        let children = params.children;
        if (typeof params.renderFooter === "function") footer = params.renderFooter(onResolve);
        if (typeof params.renderContent === "function") children = params.renderContent(onResolve);

        render(<BlankModalReduxWrapper {...params} onQuit={onQuit} children={children} footer={footer} />);
    }
    else resolve(null);
});